import React, { useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import { BaseBreadcrumbWithRender } from '@app/components/common/BaseBreadcrumb/BaseBreadcrumb';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useLocation, useNavigate } from 'react-router-dom';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';
import Receipt from '@app/components/apps/common/Receipt';

const ReceiptPage: React.FC = () => {
  const location = useLocation();
  const { receiptId, receipt_code, status } = location.state || {};
  const [currentBreadcrumName, setCurrentBreadcrumName] = useState<string>('Receipt');
  const { setBreadcrumb, clearHeaderButtons, addHeaderButton } = useHeader();
  const navigate = useNavigate();
  const handleErrorMessages = useApiErrorHandler();

  const routes = [
    {
      path: '/all-receipts',
      breadcrumbName: 'Receipts',
    },
    {
      path: '/view-receipt',
      breadcrumbName: receipt_code || 'Receipt',
    },
  ].filter((route) => route);

  useEffect(() => {
    setCurrentBreadcrumName(receipt_code || 'Receipt');
    setBreadcrumb(<BaseBreadcrumbWithRender routes={routes} separator=">" />);
    addHeaderButton(
      'Download PDF',
      <BaseButton type="primary" size="small" onClick={handleDownloadPDF}>
        Download Receipt
      </BaseButton>,
    );
    addHeaderButton(
      'Close',
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <BaseButton
          type="default"
          size="small"
          danger
          onClick={() => {
            navigate('/all-receipts');
          }}
        >
          Close
        </BaseButton>
      </div>,
    );

    return () => {
      setBreadcrumb(undefined);
      clearHeaderButtons();
    };
  }, [setBreadcrumb, clearHeaderButtons, addHeaderButton, currentBreadcrumName, routes, status, receipt_code]);

  const handleDownloadPDF = () => {
    const receiptElement = document.getElementById('receipt-content');

    if (!receiptElement) {
      console.error('Receipt content not found!');
      return;
    }

    html2canvas(receiptElement, { scale: 2 }).then((canvas) => {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const marginTop = 15;
      const marginBottom = 10;

      const usablePageHeight = pageHeight - marginTop - marginBottom;

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      const scaleRatio = pageWidth / canvasWidth;
      const scaledCanvasHeight = canvasHeight * scaleRatio;
      const sliceHeightInPx = usablePageHeight / scaleRatio;

      let position = 0;
      let pageIndex = 0;

      while (position < canvasHeight) {
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvasWidth;
        pageCanvas.height = Math.min(sliceHeightInPx, canvasHeight - position);

        const ctx = pageCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvas, 0, position, canvasWidth, pageCanvas.height, 0, 0, canvasWidth, pageCanvas.height);

          const imgData = pageCanvas.toDataURL('image/png');
          if (pageIndex > 0) pdf.addPage();

          const imgHeightInMm = pageCanvas.height * scaleRatio;

          pdf.addImage(imgData, 'PNG', 0, marginTop, pageWidth, imgHeightInMm);
        }

        position += sliceHeightInPx;
        pageIndex++;
      }

      pdf.save(`receipt-${receipt_code}.pdf`);
    });
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Receipt id={receiptId} setCurrentBreadcrumName={setCurrentBreadcrumName} />
        </Col>
      </Row>
    </>
  );
};

export default ReceiptPage;
